/**
 * Hook for fetching and managing booking price quotes
 */

import { useEffect, useState, useCallback } from "react";
import {
  getBookingQuote,
  saveBookingQuote,
} from "../services/bookingQuoteService";
import type { BookingQuoteWithComponents } from "../types/bookingQuote";
import type { PricingComponentResult } from "../components/BookingWizard/hooks/useTemplatePricing";
import type { CalculatedPrices } from "../components/BookingWizard/PricingCalculatorTypes";

interface SaveQuoteData {
  bookingId: number;
  pricingResult?: {
    components: PricingComponentResult[];
    totalCost: number;
    currency: string;
    matchedTemplate?: any | null;
    matchingCriteria?: string[];
  };
  calculatedPrices?: CalculatedPrices;
  transportType?: number;
  bookingType?: number;
  incoterm?: number;
  originPort?: number;
  destinationPort?: number;
}

export const useBookingQuote = (bookingId: number | null) => {
  const [quote, setQuote] = useState<BookingQuoteWithComponents | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchQuote = useCallback(async () => {
    if (!bookingId) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const result = await getBookingQuote(bookingId);
      
      if (result.success && result.data) {
        setQuote(result.data);
        setError(null);
      } else {
        setQuote(null);
        setError(result.error || "Failed to fetch quote");
      }
    } catch (err) {
      setQuote(null);
      setError(err instanceof Error ? err.message : "An error occurred");
    } finally {
      setLoading(false);
    }
  }, [bookingId]);

  const saveQuoteData = useCallback(
    async (data: SaveQuoteData) => {
      try {
        const result = await saveBookingQuote(data);
        if (result.success) {
          // Refresh the quote after saving
          await fetchQuote();
          return { success: true };
        }
        return {
          success: false,
          error: result.error || "Failed to save quote",
        };
      } catch (err) {
        return {
          success: false,
          error: err instanceof Error ? err.message : "Unknown error",
        };
      }
    },
    [fetchQuote]
  );

  useEffect(() => {
    if (bookingId) {
      fetchQuote();
    }
  }, [bookingId, fetchQuote]);

  return {
    quote,
    loading,
    error,
    refetch: fetchQuote,
    saveQuote: saveQuoteData,
  };
};

